// Pure sprint math. A sprint is one calendar week (Mon-Sun); a ticket belongs to
// the sprint of its creation time. Numbering counts weeks from a Monday epoch so
// sprint numbers are stable. The epoch is configurable by an admin (stored in
// Firestore); callers pass it in, falling back to DEFAULT_EPOCH_MONDAY_UTC.
// All math is in UTC to avoid DST off-by-ones at week boundaries.

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;

// Default sprint #1 = week of Monday 2026-06-01 (UTC), so the week of 2026-06-15
// is sprint #3. Overridable via the admin settings page.
export const DEFAULT_EPOCH_MONDAY_UTC = Date.UTC(2026, 5, 1);

// Midnight UTC of the calendar day a timestamp falls on.
function dayStartUtc(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

// Monday (UTC midnight) of the week containing the given timestamp. Used to snap
// an admin-picked date to a valid epoch.
export function mondayOfWeekUtc(ms: number): number {
  const start = dayStartUtc(ms);
  const day = new Date(start).getUTCDay(); // 0=Sun..6=Sat
  const back = (day + 6) % 7; // days since Monday
  return start - back * DAY_MS;
}

// 1-based sprint number for a timestamp (weeks since the epoch Monday).
export function sprintIndexFromMs(ms: number, epochMs: number = DEFAULT_EPOCH_MONDAY_UTC): number {
  return Math.floor((dayStartUtc(ms) - epochMs) / WEEK_MS) + 1;
}

// Ukrainian month names in the genitive case (for "15-19 червня").
const UA_MONTHS_GEN = [
  'січня',
  'лютого',
  'березня',
  'квітня',
  'травня',
  'червня',
  'липня',
  'серпня',
  'вересня',
  'жовтня',
  'листопада',
  'грудня',
];

// "Sprint #3 (15-19 червня)" (Mon-Fri). Spans months as "29 червня - 3 липня".
export function sprintLabel(index: number, epochMs: number = DEFAULT_EPOCH_MONDAY_UTC): string {
  const monday = new Date(epochMs + (index - 1) * WEEK_MS);
  const friday = new Date(monday.getTime() + 4 * DAY_MS);
  const md = monday.getUTCDate();
  const fd = friday.getUTCDate();
  const mMon = UA_MONTHS_GEN[monday.getUTCMonth()] ?? '';
  const fMon = UA_MONTHS_GEN[friday.getUTCMonth()] ?? '';
  const range =
    monday.getUTCMonth() === friday.getUTCMonth()
      ? `${md}-${fd} ${mMon}`
      : `${md} ${mMon} - ${fd} ${fMon}`;
  return `Sprint #${index} (${range})`;
}
